/** @jsx jsx */

/** @jsxFrag React.Fragment */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import clsx from 'clsx';

import { useId } from '@coursera/cds-common';
import type { BaseComponentProps } from '@coursera/cds-common';
import { Grid } from '@coursera/cds-core';

import PillCollection from 'bundles/browse-common/components/Pill/PillCollection';
import getPillGroupCss, { classes } from 'bundles/browse-common/components/Pill/getPillGroupCss';
import { ItemsCollectionProvider } from 'bundles/browse-common/components/Pill/items-collection';

export type PillsForScreenSize = {
  xs: number;
  sm: number;
  md: number;
  lg: number;
};

export type Props = BaseComponentProps<'div'> & {
  /**
   * Whether to collapse the group of pills. Default is false.
   * If true, the group will be collapsed on load and the expand/collapse button will be shown.
   * If false, all of the pills will be visible on load.
   */
  shouldCollapse?: boolean;
  /**
   * The number of pills to show for each screen size.
   *
   * @default { xs: 4, sm: 8, md: 6, lg: 8 }
   */
  pillsForScreenSize?: PillsForScreenSize;
  /**
   * Callback function called when the pill group is expanded.
   */
  onExpand?: () => void;
  /**
   * Callback function called when the pill group is collapsed.
   */
  onCollapse?: () => void;
  /**
   * Individual `Pill` elements used as group items.
   *
   * ⚠️The `Pill` elements **must** be direct descendants.
   *
   * @see Pill
   */
  children: React.ReactNode;
};

const PILLS_FOR_SCREEN_SIZE: PillsForScreenSize = {
  xs: 4,
  sm: 8,
  md: 6,
  lg: 8,
};

/**
 * PillGroup is a wrapper component used to group Pill components
 * that provides an easier API, and proper keyboard accessibility to the group
 *
 */
const PillGroup = ({
  shouldCollapse = false,
  pillsForScreenSize = PILLS_FOR_SCREEN_SIZE,
  onExpand,
  onCollapse,
  className,
  children,
  ...props
}: Props) => {
  const id = useId(props.id);

  return (
    <div css={getPillGroupCss}>
      <Grid container className={clsx(classes.pillGroup)} direction="row">
        <ItemsCollectionProvider commonProps={{ role: 'button', variant: 'select' }}>
          <PillCollection
            aria-live="polite"
            aria-relevant="text"
            className={clsx(classes.group, className)}
            id={id}
            role="group"
            shouldCollapse={shouldCollapse}
            pillsForScreenSize={pillsForScreenSize}
            onExpand={onExpand}
            onCollapse={onCollapse}
          >
            {children}
          </PillCollection>
        </ItemsCollectionProvider>
      </Grid>
    </div>
  );
};

export default PillGroup;
